import { useQuery } from "@tanstack/react-query";
import { fetchQuizSeriesList } from "../services/quizSeriesService";

function resolveQuizSeriesEndpoint(routeKey) {
  const normalizedRouteKey = Number(routeKey);

  if (normalizedRouteKey === 1) {
    return {
      endpoint: "/quiz/quiz-series-list",
      baseParams: {
        is_quiz: 1,
      },
    };
  }

  if (normalizedRouteKey === 4) {
    return {
      endpoint: "/quiz/quiz-series-list",
      baseParams: {
        is_quiz: 2,
      },
    };
  }

  if (normalizedRouteKey === 3) {
    return {
      endpoint: "/quiz/get-quiz-pyp-list",
      baseParams: {},
    };
  }

  if (normalizedRouteKey === 2) {
    return {
      endpoint: "/quiz/quiz-series-list",
      baseParams: {
        is_quiz: 0,
      },
    };
  }

  return {
    endpoint: null,
    baseParams: {},
  };
}

function normalizeQuizItem(item, index, routeKey) {
  if (Number(routeKey) === 2) {
    const totalQuizzes = Number(item?.total_quiz || 0);
    const totalFreeQuizzes = Number(item?.total_free_quiz || 0);
    const totalAttemptedQuizzes = Number(item?.total_attempted_quiz || 0);
    const progress = totalQuizzes > 0 ? Math.round((totalAttemptedQuizzes / totalQuizzes) * 100) : 0;
    const paymentStatus = Number(item?.payment_status || 0);
    const enrolledStatus = Number(item?.enrolled_status || 0);
    const isFree = paymentStatus === 0 && totalFreeQuizzes > 0;
    const startDate = item?.start_date || "";
    const endDate = item?.end_date || "";
    const mdExams = Array.isArray(item?.md_exams) ? item.md_exams : [];

    return {
      id: item?.series_id || `${item?.name || "series"}-${index}`,
      number: String(index + 1).padStart(2, "0"),
      title: item?.name || "Untitled Series",
      description: item?.description || "",
      plan: isFree ? "Free" : "Paid",
      planClass: isFree ? "free" : "prime",
      date: startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate || "TBA",
      duration: `${totalQuizzes} Tests`,
      status: enrolledStatus ? "Enrolled" : paymentStatus ? "Available" : "Locked",
      questions: String(totalQuizzes),
      attempts: String(totalAttemptedQuizzes),
      attemptsLeft: String(Math.max(totalQuizzes - totalAttemptedQuizzes, 0)),
      language: item?.available_languages || "English",
      marks: String(Number(item?.total_enrollments || 0)),
      freeCount: String(totalFreeQuizzes),
      progress,
      coins: "",
      mdExams,
      examCount: mdExams.length,
    };
  }

  const isFree = item?.free_paid === "F";
  const isLocked = !isFree && Number(item?.payment_status || 0) !== 1;
  const attemptedQuestions = Number(item?.total_attempted_question || 0);
  const totalQuestions = Number(item?.total_questions || 0);
  const progress = totalQuestions > 0 ? Math.round((attemptedQuestions / totalQuestions) * 100) : 0;
  const liveType = item?.quiz_live_type === "L" ? "Live" : "Self-Paced";
  const pendingQuizInfo = item?.pending_quiz_info || null;
  const pendingAttempt =
    (Array.isArray(item?.attempted_quiz_list) ? item.attempted_quiz_list : []).find(
      (attempt) => String(attempt?.quiz_action || "").trim().toLowerCase() === "pending"
    ) || null;
  const resolvedPendingQuizInfo = pendingQuizInfo || pendingAttempt;

  return {
    id: item?.quiz_id || `${item?.quiz_name || "quiz"}-${index}`,
    quizId: Number(item?.quiz_id || 0),
    freePaid: String(item?.free_paid || '').toUpperCase(),
    paymentStatus: Number(item?.payment_status || 0),
    coinUnlockCost: Number(item?.coin_unlock_cost || 0),
    canUnlockWithAdCoins: [true, 1, '1'].includes(item?.can_unlock_with_ad_coins),
    subscriptionMessage: item?.subscription_message || '',
    isToday: [true, 1, '1'].includes(item?.is_today) || [true, 1, '1'].includes(item?.is_today_quiz),
    number: String(index + 1).padStart(2, "0"),
    title: item?.quiz_name || "Untitled Quiz",
    description: item?.quiz_description || "",
    plan: isFree ? "Free" : "Paid",
    planClass: isFree ? "free" : "prime",
    date: item?.start_date || "TBA",
    duration: `${Number(item?.duration || 0)} Minutes`,
    status: isLocked ? "Locked" : item?.solved_at || liveType,
    questions: String(totalQuestions),
    attempts: String(Number(item?.total_attempts || 0)),
    language: item?.available_languages || "English",
    marks: String(Number(item?.total_marks || 0)),
    progress,
    coins:
      isLocked && item?.can_unlock_with_ad_coins
        ? `${Number(item?.coin_unlock_cost || 0)} Coins`
        : "",
    statesId: Number(item?.states_id || item?.state_id || 0),
    categoryId: Number(item?.category_id || 0),
    pendingQuizInfo: resolvedPendingQuizInfo
      ? {
          assesment_batch_id: Number(resolvedPendingQuizInfo?.assesment_batch_id || 0),
          quiz_action: resolvedPendingQuizInfo?.quiz_action || "Pending",
          quiz_id: Number(
            resolvedPendingQuizInfo?.quiz_id || item?.quiz_id || 0
          ),
          question_id: Number(resolvedPendingQuizInfo?.question_id || 0),
          attempted_count: Number(resolvedPendingQuizInfo?.attempted_count || 0),
          quiz_mode: String(resolvedPendingQuizInfo?.quiz_mode || "").trim().toUpperCase(),
        }
      : null,
  };
}

function resolveQuizListFromPayload(routeKey, payload = {}) {
  if (Number(routeKey) === 2) {
    return Array.isArray(payload?.series_list) ? payload.series_list : [];
  }

  if (Number(routeKey) === 3) {
    return Array.isArray(payload?.quiz_pyp_list) ? payload.quiz_pyp_list : [];
  }

  return Array.isArray(payload?.quiz_list) ? payload.quiz_list : [];
}

export function useQuizSeriesList(params = {}, options = {}) {
  const { endpoint, baseParams } = resolveQuizSeriesEndpoint(params?.route_key);
  const queryParams = {
    ...params,
    ...baseParams,
    ...(Number(params.route_key) === 2 ? { category_id: 1 } : {}),
  };

  return useQuery({
    queryKey: ["quizSeriesList", endpoint, queryParams],
    queryFn: async () => {
      if (!endpoint) {
        return {
          bannerImage: "",
          stateName: "",
          categoryName: "",
          seriesName: "",
          quizList: [],
          totalRecords: 0,
          currentPage: 1,
          totalPages: 1,
          advertEarnedCoins: 0,
        };
      }

      const { payload } = await fetchQuizSeriesList(queryParams, endpoint);
      let rawQuizList = resolveQuizListFromPayload(params?.route_key, payload);
      if (Number(params?.route_key) === 1 || Number(params?.route_key) === 4) {
        const today = Array.isArray(payload?.today_quiz_list) ? payload.today_quiz_list : [];
        const todayIds = new Set(today.map((quiz) => String(quiz.quiz_id)));
        rawQuizList = [
          ...today.map((quiz) => ({ ...quiz, is_today: 1 })),
          ...rawQuizList.filter((quiz) => !todayIds.has(String(quiz.quiz_id))),
        ];
      }
      const pagination = payload?.pagination || {};

      return {
        bannerImage: payload?.banner_image || "",
        stateName: payload?.state_name || "",
        categoryName: payload?.category_name || "",
        seriesName:
          payload?.series_name ||
          payload?.mock_test_name ||
          payload?.pyp_name ||
          payload?.category_name ||
          "",
        quizList: rawQuizList.map((item, index) => normalizeQuizItem(item, index, params?.route_key)),
        totalRecords: Number(payload?.pagination?.totalRecords || rawQuizList.length || 0),
        currentPage: Number(pagination?.currentPage || queryParams?.page || 1),
        totalPages: Number(pagination?.totalPages || 1),
        advertEarnedCoins: Number(payload?.advert_earned_coins || 0),
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    placeholderData: (previousData) =>
      previousData ?? {
        bannerImage: "",
        stateName: "",
        categoryName: "",
        seriesName: "",
        quizList: [],
        totalRecords: 0,
        currentPage: 1,
        totalPages: 1,
        advertEarnedCoins: 0,
      },
  });
}
